import { useState } from 'react';
import Modal from './Modal';
import CustomSelect from './CustomSelect';
import StoryPointsInput from './StoryPointsInput';
import FormField from './shared/FormField';
import TypeSelectorButtons from './shared/TypeSelectorButtons';
import RetroColumnPicker from './shared/RetroColumnPicker';
import { TYPE_CONFIG, TYPES_SIMPLE, HIERARCHY_TYPES_SIMPLE, PRIORITY_OPTIONS, RETRO_COLUMNS } from '../constants';
import { buildUserOptions } from '../utils';

export default function AddItemModal({ onClose, onAdd, users, isKanban, isRetro, parentId, allowedTypes, parentContext }) {
  const typeOptions = allowedTypes
    ? HIERARCHY_TYPES_SIMPLE.filter((t) => allowedTypes.includes(t.value))
    : (isKanban ? TYPES_SIMPLE : HIERARCHY_TYPES_SIMPLE);
  const [type, setType] = useState(typeOptions[0]?.value || 'task');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [storyPoints, setStoryPoints] = useState('');
  const [assigneeId, setAssigneeId] = useState('');
  const [priority, setPriority] = useState('medium');
  const [retroColumn, setRetroColumn] = useState(RETRO_COLUMNS[0].status);
  const [saving, setSaving] = useState(false);

  const userOptions = buildUserOptions(users);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    setSaving(true);
    if (isRetro) {
      await onAdd({
        title: title.trim(),
        description: description.trim(),
        status: retroColumn,
        priority: null,
        assigneeId: null,
        storyPoints: null,
        parentId: null,
      });
    } else {
      await onAdd({
        type,
        title: title.trim(),
        description: description.trim(),
        storyPoints: storyPoints === '' ? null : Number(storyPoints),
        assigneeId: assigneeId || null,
        priority: isKanban ? priority : null,
        status: 'not_started',
        parentId: parentId || null,
      });
    }
    setSaving(false);
    onClose();
  };

  const modalTitle = isRetro
    ? 'Add Retro Note'
    : (parentContext ? `Add child to ${parentContext.title}` : 'Add Item');

  if (isRetro) {
    return (
      <Modal title={modalTitle} onClose={onClose}>
        <form onSubmit={handleSubmit} className="add-item-form">
          <FormField label="Column">
            <div className="type-selector">
              <RetroColumnPicker
                value={retroColumn}
                onChange={setRetroColumn}
                getButtonClass={(v, active) =>
                  `type-option retro-col-option retro-col-option--${v}${active ? ' active' : ''}`
                }
              />
            </div>
          </FormField>

          <FormField label="Title">
            <input
              className="form-input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Brief summary..."
              required
              autoFocus
            />
          </FormField>

          <FormField label="Note">
            <textarea
              className="form-input retro-note-textarea"
              rows={7}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Explain in detail..."
            />
          </FormField>

          <div className="modal-footer">
            <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving || !title.trim()}>
              {saving ? 'Adding...' : 'Add Note'}
            </button>
          </div>
        </form>
      </Modal>
    );
  }

  return (
    <Modal title={modalTitle} onClose={onClose}>
      {parentContext && (
        <div className="add-item-parent-context">
          <span style={{ color: TYPE_CONFIG[parentContext.type]?.color }}>
            {TYPE_CONFIG[parentContext.type]?.label}
          </span>
          <span>&rarr;</span>
          <span>{parentContext.title}</span>
        </div>
      )}
      <form onSubmit={handleSubmit} className="add-item-form">
        <FormField label="Type">
          <TypeSelectorButtons typeOptions={typeOptions} value={type} onChange={setType} iconSize={18} />
        </FormField>

        <FormField label="Title">
          <input
            className="form-input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Enter title"
            required
            autoFocus
          />
        </FormField>

        <FormField label="Description">
          <textarea
            className="form-input"
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Optional description"
          />
        </FormField>

        <div className="form-row">
          <FormField label="Story Points">
            <StoryPointsInput value={storyPoints} onChange={setStoryPoints} placeholder="Optional" />
          </FormField>
          <FormField label="Assign To">
            <CustomSelect
              value={assigneeId}
              onChange={setAssigneeId}
              options={userOptions}
              placeholder="Unassigned"
            />
          </FormField>
        </div>
        {isKanban && (
          <FormField label="Priority">
            <CustomSelect value={priority} onChange={setPriority} options={PRIORITY_OPTIONS} />
          </FormField>
        )}

        <div className="modal-footer">
          <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={saving || !title.trim()}>
            {saving ? 'Adding...' : 'Add Item'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
